/**
 * <gift-bag> — opt-in gift bag as a real cart line item.
 */
if (!customElements.get('gift-bag')) {
  class GiftBag extends HTMLElement {
    constructor() {
      super();
      this.GIFT_PROP = '_gift_bag';
      this.busy = false;
      this.syncToken = 0;
      this.onClick = this.onClick.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
      this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      this.variantId = this.getAttribute('data-gift-bag-variant');
      this.button = this.querySelector('[data-gift-add]');
      this.qty = this.querySelector('[data-gift-qty]');
      this.qtyValue = this.querySelector('[data-gift-qty-value]');
      this.giftLineKey = null;
      this.currentQty = 0;

      this.addEventListener('click', this.onClick);
      document.addEventListener('theme:cart:change', this.onCartChange);

      this.externalSync();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.onCartChange);
    }

    isGiftLine(item) {
      return item && item.properties && item.properties[this.GIFT_PROP];
    }

    stateFromCart(cart) {
      const line = ((cart && cart.items) || []).find((i) => this.isGiftLine(i));
      return line ? { key: line.key, quantity: line.quantity } : { key: null, quantity: 0 };
    }

    fetchCart() {
      return fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    }

    apply(state) {
      this.giftLineKey = state.key;
      this.currentQty = state.quantity;
      const added = Boolean(state.key);
      this.classList.toggle('is-selected', added);

      if (this.qty) this.qty.setAttribute('aria-hidden', added ? 'false' : 'true');
      if (this.qtyValue) this.qtyValue.textContent = this.currentQty;

      if (!this.button) return;
      this.button.classList.toggle('btn--outline', added);
      this.button.classList.toggle('btn--solid', !added);
      this.button.textContent = added ? this.button.dataset.labelRemove : this.button.dataset.labelAdd;
    }

    onClick(e) {
      if (e.target.closest('[data-gift-qty-minus]')) {
        e.preventDefault();
        this.changeQty(-1);
        return;
      }
      if (e.target.closest('[data-gift-qty-plus]')) {
        e.preventDefault();
        this.changeQty(1);
        return;
      }
      if (!e.target.closest('[data-gift-add]')) return;
      e.preventDefault();
      if (this.giftLineKey) {
        this.remove();
      } else {
        this.add();
      }
    }

    changeQty(delta) {
      if (!this.giftLineKey) return;
      const next = this.currentQty + delta;
      if (next < 1) {
        this.remove();
      } else {
        this.mutate(() => this.setLineQty(next));
      }
    }

    /**
     * External cart changes (e.g. removed from the drawer). Own mutations apply
     * their own authoritative response, so skip the echo they trigger.
     */
    onCartChange() {
      if (this.scrollAfterBuild) {
        this.scrollAfterBuild = false;
        this.scrollDrawerToStart();
      }

      if (this.skipNextExternalSync) {
        this.skipNextExternalSync = false;
        return;
      }

      const delay = this.revealDelay();
      if (delay > 0) {
        setTimeout(() => this.externalSync(), delay);
      } else {
        this.externalSync();
      }
    }

    externalSync() {
      const token = ++this.syncToken;
      this.fetchCart().then((cart) => {
        if (token !== this.syncToken) return;
        this.apply(this.stateFromCart(cart));
      });
    }

    revealDelay() {
      const block = this.closest('[data-animation]');
      if (!block) return 0;
      const delay = parseInt(block.getAttribute('data-animation-delay'), 10) || 0;
      const duration = parseInt(block.getAttribute('data-animation-duration'), 10) || 0;
      return delay + duration;
    }

    addLine() {
      const body = { id: this.variantId, quantity: 1, properties: {} };
      body.properties[this.GIFT_PROP] = 'true';
      return fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((item) => ({ key: item.key, quantity: item.quantity }));
    }

    setLineQty(quantity) {
      return fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.giftLineKey, quantity }),
      })
        .then((r) => r.json())
        .then((cart) => this.stateFromCart(cart));
    }

    add() {
      this.scrollAfterBuild = true;
      this.mutate(() => this.addLine());
    }

    remove() {
      if (!this.giftLineKey) return;
      this.mutate(() => this.setLineQty(0));
    }

    scrollDrawerToStart() {
      const body = document.querySelector('[data-cart-drawer-body]');
      if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /**
     * Apply state straight from the mutation response — no round-trip through
     * the global cart event or a second fetch — then let the drawer re-render.
     */
    mutate(action) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.syncToken++;

      action()
        .then((state) => {
          this.apply(state);
          this.skipNextExternalSync = true;
          document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        })
        .catch((err) => console.log(err))
        .then(() => {
          this.busy = false;
          this.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('gift-bag', GiftBag);
}
